import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { calculateLeaseLength, calculateNewLeaseDatesFromLease, formatLeaseLengthFromDates, formatLeaseLengthValue } from '@/helpers/leaseHelpers';
import {
  currentTenantStatusLabel,
  nextTenantStatusLabel,
  unitStatusGroup,
  type ResolvedUnitStatus,
} from '@/helpers/unitStatus';
import {
  getComparableUnits,
  calculateGroupSummary,
  ComparableGroup,
  GroupSummary,
} from '@/components/BuildingTabs/PricingTool/comparablesUtils';
import { analyzeTrends, TrendsData } from '@/components/BuildingTabs/PricingTool/trendsUtils';

/**
 * Calculate total concessions, preferring stored totalConcessionAmount.
 * Falls back to type-aware calculation from concessions array.
 */
function calculateConcessions(lease: Lease): number {
  if (lease.totalConcessionAmount !== undefined && lease.totalConcessionAmount > 0) {
    return lease.totalConcessionAmount;
  }
  if (!Array.isArray(lease.concessions)) {
    return ((lease.concessions as any) || 0) * lease.monthlyRent;
  }
  return Math.round(
    lease.concessions.reduce((sum: number, c: any) => {
      if (c.type === 'fixed') return sum + (c.amount || 0);
      if (c.type === 'percentage') return sum + ((c.amount || 0) * lease.monthlyRent / 100);
      return sum + ((c.amount || 0) * lease.monthlyRent); // rentMultiplier
    }, 0) * 100
  ) / 100;
}

/**
 * Calculate lease length from dates with proper rounding
 * Rounds to nearest month if within ~3 days (0.1 of a month)
 */
function calculateLeaseLengthFromDates(startDate: Date | null, endDate: Date | null): number {
  if (!startDate || !endDate) return 0;

  const exactMonths = dayjs(endDate).diff(dayjs(startDate), 'month', true);

  // Round to nearest month if within ~3 days (0.1 of a month ≈ 3 days)
  const roundedMonths = Math.round(exactMonths);
  const difference = Math.abs(exactMonths - roundedMonths);

  if (difference <= 0.1) {
    return roundedMonths;
  }

  return exactMonths;
}

/**
 * Convert concession dollar amount to months (as a multiplier of monthly rent)
 */
function concessionsToMonths(concessionAmount: number, monthlyRent: number): number {
  if (monthlyRent <= 0) return 0;
  return Math.round((concessionAmount / monthlyRent) * 10) / 10;
}

export interface CurrentMetrics {
  monthlyRent: number;
  leaseLength: number;
  concessions: number;
  effectiveMonthlyRent: number;
  effectiveAnnualRent: number;
  effectiveAnnualRentPerSqft: number;
  annualRent: number;
  annualRentPerSqft: number;
  sqft: number;
}

export interface ProposedMetrics {
  monthlyRent: number;
  leaseLength: number;
  concessions: number;
  effectiveMonthlyRent: number;
  effectiveAnnualRent: number;
  effectiveAnnualRentPerSqft: number;
  annualRent: number;
  annualRentPerSqft: number;
  netIncreaseAmount?: number;
  netIncreasePercent?: number;
}

export interface UnitPricingData {
  unit: Unit;
  currentLease: Lease;
  currentMetrics: CurrentMetrics;
  proposedMetrics: ProposedMetrics;
  comparables?: ComparableGroup;
  trends?: TrendsData | null;
}

// Style constants
const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF1E3A5F' }, // Dark blue
};

const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: 'FFFFFFFF' }, // White
  size: 11,
};

const SUBHEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF4A6FA5' }, // Lighter blue
};

const TITLE_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  size: 14,
  color: { argb: 'FF1E3A5F' },
};

const SECTION_TITLE_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  size: 12,
  color: { argb: 'FF1E3A5F' },
};

const SUMMARY_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE8EEF4' }, // Light blue/gray
};

const POSITIVE_FONT: Partial<ExcelJS.Font> = {
  color: { argb: 'FF2E7D32' }, // Green
  bold: true,
};

const NEGATIVE_FONT: Partial<ExcelJS.Font> = {
  color: { argb: 'FFC62828' }, // Red
  bold: true,
};

const THIN_BORDER: Partial<ExcelJS.Border> = {
  style: 'thin',
  color: { argb: 'FFB0BEC5' },
};

const ALL_BORDERS: Partial<ExcelJS.Borders> = {
  top: THIN_BORDER,
  left: THIN_BORDER,
  bottom: THIN_BORDER,
  right: THIN_BORDER,
};

/**
 * Calculate metrics for a lease
 */
export function calculateLeaseMetrics(unit: Unit, lease: Lease): CurrentMetrics | null {
  const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
  const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);
  if (!startDate || !endDate) return null;

  const leaseLength = calculateLeaseLength(lease);
  const sqft = unit.sqft || 1;

  const totalConcessions = calculateConcessions(lease);

  const effectiveMonthlyRent = leaseLength > 0
    ? Math.round((lease.monthlyRent - (totalConcessions / leaseLength)) * 100) / 100
    : lease.monthlyRent;
  const effectiveAnnualRent = effectiveMonthlyRent * 12;
  const effectiveAnnualRentPerSqft = Math.round((effectiveAnnualRent / sqft) * 100) / 100;
  const annualRent = lease.monthlyRent * 12;
  const annualRentPerSqft = annualRent / sqft;

  return {
    monthlyRent: lease.monthlyRent,
    leaseLength,
    concessions: totalConcessions,
    effectiveMonthlyRent,
    effectiveAnnualRent,
    effectiveAnnualRentPerSqft,
    annualRent,
    annualRentPerSqft,
    sqft,
  };
}

/**
 * Apply header style to a row
 */
function applyHeaderStyle(row: ExcelJS.Row): void {
  row.eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.border = ALL_BORDERS;
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  row.height = 22;
}

/**
 * Apply data cell style
 */
function applyDataStyle(row: ExcelJS.Row): void {
  row.eachCell((cell) => {
    cell.border = ALL_BORDERS;
    cell.alignment = { vertical: 'middle' };
  });
}

/**
 * Apply summary row style
 */
function applySummaryStyle(row: ExcelJS.Row): void {
  row.eachCell((cell) => {
    cell.fill = SUMMARY_FILL;
    cell.font = { bold: true };
    cell.border = ALL_BORDERS;
    cell.alignment = { vertical: 'middle' };
  });
}

/**
 * Format currency value
 */
function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Create the Before/After comparison worksheet
 */
function createBeforeAfterSheet(
  workbook: ExcelJS.Workbook,
  unit: Unit,
  currentMetrics: CurrentMetrics,
  proposedMetrics: ProposedMetrics
): void {
  const sheet = workbook.addWorksheet('Before-After');

  // Set column widths
  sheet.columns = [
    { width: 25 },
    { width: 18 },
    { width: 18 },
    { width: 15 },
    { width: 12 },
  ];

  // Title
  const titleRow = sheet.addRow(['BEFORE/AFTER COMPARISON']);
  titleRow.getCell(1).font = TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 5);
  titleRow.height = 28;

  sheet.addRow([]);

  // Unit Information section
  const unitInfoRow = sheet.addRow(['Unit Information']);
  unitInfoRow.getCell(1).font = SECTION_TITLE_FONT;
  sheet.mergeCells(unitInfoRow.number, 1, unitInfoRow.number, 2);

  const unitNameRow = sheet.addRow(['Unit Name', unit.name || unit.id]);
  unitNameRow.getCell(1).font = { bold: true };
  unitNameRow.eachCell((cell) => { cell.border = ALL_BORDERS; });

  const bedroomsRow = sheet.addRow(['Bedrooms', unit.numBedrooms]);
  bedroomsRow.getCell(1).font = { bold: true };
  bedroomsRow.eachCell((cell) => { cell.border = ALL_BORDERS; });

  const sqftRow = sheet.addRow(['Square Feet', unit.sqft]);
  sqftRow.getCell(1).font = { bold: true };
  sqftRow.eachCell((cell) => { cell.border = ALL_BORDERS; });

  sheet.addRow([]);

  // Comparison table header
  const headerRow = sheet.addRow(['Metric', 'Current', 'Proposed', 'Change', 'Change %']);
  applyHeaderStyle(headerRow);

  // Metrics data
  const metricsData = [
    {
      metric: 'Monthly Rent',
      current: currentMetrics.monthlyRent,
      proposed: proposedMetrics.monthlyRent,
      isCurrency: true,
    },
    {
      metric: 'Lease Length',
      current: currentMetrics.leaseLength,
      proposed: proposedMetrics.leaseLength,
      isCurrency: false,
    },
    {
      metric: 'Concessions ($)',
      current: currentMetrics.concessions,
      proposed: proposedMetrics.concessions,
      isCurrency: true,
      invertPositive: true,
    },
    {
      metric: 'Concessions (mo)',
      current: concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent),
      proposed: concessionsToMonths(proposedMetrics.concessions, proposedMetrics.monthlyRent),
      isCurrency: false,
      invertPositive: true,
    },
    {
      metric: 'Effective Monthly Rent',
      current: currentMetrics.effectiveMonthlyRent,
      proposed: proposedMetrics.effectiveMonthlyRent,
      isCurrency: true,
    },
    {
      metric: 'Annual Rent',
      current: currentMetrics.annualRent,
      proposed: proposedMetrics.annualRent,
      isCurrency: true,
    },
    {
      metric: 'Gross PSF',
      current: currentMetrics.annualRentPerSqft,
      proposed: proposedMetrics.annualRentPerSqft,
      isCurrency: true,
    },
    {
      metric: 'Net PSF',
      current: currentMetrics.effectiveAnnualRentPerSqft,
      proposed: proposedMetrics.effectiveAnnualRentPerSqft,
      isCurrency: true,
    },
  ];

  metricsData.forEach((data) => {
    const change = data.proposed - data.current;
    const changePercent = data.current !== 0 ? (change / data.current) * 100 : 0;
    const isPositive = data.invertPositive ? change <= 0 : change >= 0;

    const isLeaseLength = data.metric === 'Lease Length';
    const formatValue = (v: number) =>
      data.isCurrency ? formatCurrency(v) : isLeaseLength ? formatLeaseLengthValue(v) : v;

    const row = sheet.addRow([
      data.metric,
      formatValue(data.current),
      formatValue(data.proposed),
      data.isCurrency ? formatCurrency(change) : isLeaseLength ? Math.round(change) : change,
      isLeaseLength ? '-' : `${changePercent.toFixed(1)}%`,
    ]);

    applyDataStyle(row);
    row.getCell(1).font = { bold: true };

    // Apply color to change columns
    if (!isLeaseLength) {
      row.getCell(4).font = isPositive ? POSITIVE_FONT : NEGATIVE_FONT;
      row.getCell(5).font = isPositive ? POSITIVE_FONT : NEGATIVE_FONT;
    }
  });
}

/**
 * Create comparable units worksheet
 */
function createComparablesSheet(
  workbook: ExcelJS.Workbook,
  sheetName: string,
  groupName: string,
  units: Unit[],
  leaseMap: Map<string, Lease>,
  summary: GroupSummary | null
): void {
  const sheet = workbook.addWorksheet(sheetName);

  // Set column widths
  sheet.columns = [
    { width: 15 }, // Unit
    { width: 10 }, // Line
    { width: 10 }, // BR
    { width: 10 }, // Sqft
    { width: 14 }, // Monthly Rent
    { width: 12 }, // Gross PPSF
    { width: 14 }, // Concessions ($)
    { width: 12 }, // Conc. (mo)
    { width: 14 }, // Net Rent
    { width: 12 }, // Net PPSF
    { width: 10 }, // Length
    { width: 14 }, // Lease Start
    { width: 14 }, // Lease End
  ];

  // Title
  const titleRow = sheet.addRow([`COMPARABLE UNITS - ${groupName.toUpperCase()}`]);
  titleRow.getCell(1).font = TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 13);
  titleRow.height = 28;

  sheet.addRow([]);

  // Header row
  const headerRow = sheet.addRow([
    'Unit', 'Line', 'BR', 'Sqft', 'Monthly Rent', 'Gross PPSF', 'Conc. ($)', 'Conc. (mo)', 'Net Rent', 'Net PPSF', 'Length', 'Lease Start', 'Lease End'
  ]);
  applyHeaderStyle(headerRow);

  // Data rows
  units.forEach((unit) => {
    const lease = leaseMap.get(unit.id);
    if (!lease) return;

    const sqft = unit.sqft || 1;
    const annualRent = lease.monthlyRent * 12;
    const annualRentPerSqft = annualRent / sqft;

    const totalConcessions = calculateConcessions(lease);

    const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
    const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);
    const leaseLength = calculateLeaseLengthFromDates(startDate, endDate);
    const effectiveMonthlyRent = leaseLength > 0 ? lease.monthlyRent - totalConcessions / leaseLength : lease.monthlyRent;
    const effectiveAnnualRent = effectiveMonthlyRent * 12;
    const effectiveAnnualRentPerSqft = effectiveAnnualRent / sqft;

    const row = sheet.addRow([
      unit.name || unit.id,
      unit.type || unit.line || '-',
      unit.numBedrooms,
      sqft,
      formatCurrency(lease.monthlyRent),
      formatCurrency(Math.round(annualRentPerSqft * 100) / 100),
      formatCurrency(Math.round(totalConcessions * 100) / 100),
      concessionsToMonths(totalConcessions, lease.monthlyRent),
      formatCurrency(Math.round(effectiveMonthlyRent * 100) / 100),
      formatCurrency(Math.round(effectiveAnnualRentPerSqft * 100) / 100),
      formatLeaseLengthFromDates(startDate, endDate),
      startDate ? dayjs(startDate).format('MM/DD/YYYY') : '-',
      endDate ? dayjs(endDate).format('MM/DD/YYYY') : '-',
    ]);

    applyDataStyle(row);
  });

  // Summary rows
  if (summary) {
    sheet.addRow([]);

    const avgRow = sheet.addRow([
      'AVERAGES', '-', '-', '-',
      formatCurrency(summary.avgRent),
      formatCurrency(summary.avgPPSF),
      '-', '-',
      formatCurrency(summary.avgEffectiveRent),
      formatCurrency(summary.avgEffectivePPSF),
      '-', '-', '-',
    ]);
    applySummaryStyle(avgRow);

    const minRow = sheet.addRow([
      'MIN', '-', '-', '-',
      formatCurrency(summary.minRent),
      formatCurrency(summary.minPPSF),
      '-', '-',
      formatCurrency(summary.minEffectiveRent),
      formatCurrency(summary.minEffectivePPSF),
      '-', '-', '-',
    ]);
    applySummaryStyle(minRow);

    const maxRow = sheet.addRow([
      'MAX', '-', '-', '-',
      formatCurrency(summary.maxRent),
      formatCurrency(summary.maxPPSF),
      '-', '-',
      formatCurrency(summary.maxEffectiveRent),
      formatCurrency(summary.maxEffectivePPSF),
      '-', '-', '-',
    ]);
    applySummaryStyle(maxRow);
  }
}

/**
 * Add a detailed comparables table to an existing sheet
 */
function addComparablesTableToSheet(
  sheet: ExcelJS.Worksheet,
  groupName: string,
  units: Unit[],
  leaseMap: Map<string, Lease>,
  currentUnitId: string
): void {
  if (units.length === 0) return;

  // Section title
  const titleRow = sheet.addRow([`${groupName.toUpperCase()} COMPARISON`]);
  titleRow.getCell(1).font = SECTION_TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 12);

  // Header row for comparables table
  const headerRow = sheet.addRow([
    'Unit', 'Line', 'BR', 'Sqft', 'Rent', 'Gross PPSF', 'Conc. (mo)', 'Net Rent', 'Net PPSF', 'Length', 'Lease Start', 'Lease End'
  ]);
  applyHeaderStyle(headerRow);

  // Data rows
  units.forEach((unit) => {
    const lease = leaseMap.get(unit.id);
    if (!lease) return;

    const sqft = unit.sqft || 1;
    const annualRent = lease.monthlyRent * 12;
    const annualRentPerSqft = annualRent / sqft;

    const totalConcessions = calculateConcessions(lease);

    const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
    const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);
    const leaseLength = calculateLeaseLengthFromDates(startDate, endDate);
    const effectiveMonthlyRent = leaseLength > 0 ? lease.monthlyRent - totalConcessions / leaseLength : lease.monthlyRent;
    const effectiveAnnualRent = effectiveMonthlyRent * 12;
    const effectiveAnnualRentPerSqft = effectiveAnnualRent / sqft;

    const row = sheet.addRow([
      unit.name || unit.id,
      unit.type || unit.line || '-',
      unit.numBedrooms,
      sqft,
      formatCurrency(lease.monthlyRent),
      formatCurrency(Math.round(annualRentPerSqft * 100) / 100),
      concessionsToMonths(totalConcessions, lease.monthlyRent),
      formatCurrency(Math.round(effectiveMonthlyRent * 100) / 100),
      formatCurrency(Math.round(effectiveAnnualRentPerSqft * 100) / 100),
      formatLeaseLengthFromDates(startDate, endDate),
      startDate ? dayjs(startDate).format('MM/DD/YYYY') : '-',
      endDate ? dayjs(endDate).format('MM/DD/YYYY') : '-',
    ]);

    applyDataStyle(row);

    // Highlight current unit if it appears in the comparison
    if (unit.id === currentUnitId) {
      row.eachCell((cell) => {
        cell.font = { bold: true };
      });
    }
  });

  // Add spacing after the table
  sheet.addRow([]);
}

/**
 * Create market trends worksheet
 */
function createTrendsSheet(workbook: ExcelJS.Workbook, trends: TrendsData): void {
  const sheet = workbook.addWorksheet('Market Trends');

  // Set column widths
  sheet.columns = [
    { width: 25 },
    { width: 20 },
    { width: 20 },
    { width: 15 },
    { width: 12 },
  ];

  // Title
  const titleRow = sheet.addRow(['MARKET TRENDS']);
  titleRow.getCell(1).font = TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 5);
  titleRow.height = 28;

  sheet.addRow([]);

  // Subtitle
  const subtitleRow = sheet.addRow(['Recent Lease Analysis (Last 3 Months vs Same Period Last Year)']);
  subtitleRow.getCell(1).font = SECTION_TITLE_FONT;
  sheet.mergeCells(subtitleRow.number, 1, subtitleRow.number, 5);

  sheet.addRow([]);

  // Header row
  const headerRow = sheet.addRow(['Metric', 'Recent (Last 3 mo)', 'Historical (Last Year)', 'Change', 'Change %']);
  applyHeaderStyle(headerRow);

  // Average Monthly Rent row
  const rentRow = sheet.addRow([
    'Average Monthly Rent',
    formatCurrency(Math.round(trends.recentAvgRent * 100) / 100),
    formatCurrency(Math.round(trends.historicalAvgRent * 100) / 100),
    formatCurrency(Math.round(trends.rentChange * 100) / 100),
    `${trends.rentChangePercent.toFixed(1)}%`,
  ]);
  applyDataStyle(rentRow);
  rentRow.getCell(1).font = { bold: true };
  rentRow.getCell(4).font = trends.rentChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;
  rentRow.getCell(5).font = trends.rentChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;

  // Average PPSF row
  const ppsfRow = sheet.addRow([
    'Average PPSF',
    formatCurrency(Math.round(trends.recentAvgPPSF * 100) / 100),
    formatCurrency(Math.round(trends.historicalAvgPPSF * 100) / 100),
    formatCurrency(Math.round(trends.ppsfChange * 100) / 100),
    `${trends.ppsfChangePercent.toFixed(1)}%`,
  ]);
  applyDataStyle(ppsfRow);
  ppsfRow.getCell(1).font = { bold: true };
  ppsfRow.getCell(4).font = trends.ppsfChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;
  ppsfRow.getCell(5).font = trends.ppsfChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;

  sheet.addRow([]);

  // Lease count row
  const countRow = sheet.addRow(['Recent Lease Count', trends.recentLeaseCount]);
  countRow.getCell(1).font = { bold: true };
  countRow.eachCell((cell) => { cell.border = ALL_BORDERS; });
}

/**
 * Create summary sheet for multiple units
 */
function createMultiUnitSummarySheet(
  workbook: ExcelJS.Workbook,
  unitsData: UnitPricingData[]
): void {
  const sheet = workbook.addWorksheet('Summary');

  // Set column widths
  sheet.columns = [
    { width: 15 }, // Unit
    { width: 8 },  // BR
    { width: 8 },  // Sqft
    { width: 16 }, // Current Monthly Rent
    { width: 16 }, // Proposed Monthly Rent
    { width: 12 }, // Rent Change
    { width: 12 }, // Rent Change %
    { width: 14 }, // Current Eff. Rent
    { width: 14 }, // Proposed Eff. Rent
    { width: 14 }, // Eff. Rent Change
    { width: 14 }, // Eff. Rent Change %
    { width: 14 }, // Current Gross PSF
    { width: 14 }, // Proposed Gross PSF
    { width: 14 }, // Current Net PSF
    { width: 14 }, // Proposed Net PSF
    { width: 14 }, // Concessions ($)
    { width: 12 }, // Conc. (mo)
    { width: 10 }, // Lease Length
    { width: 14 }, // Current Lease Start
    { width: 14 }, // Current Lease End
    { width: 14 }, // Proposed Lease Start
    { width: 14 }, // Proposed Lease End
  ];

  // Title
  const titleRow = sheet.addRow(['DRAFT LEASES SUMMARY']);
  titleRow.getCell(1).font = TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 22);
  titleRow.height = 28;

  sheet.addRow([]);

  // Generated date
  const dateRow = sheet.addRow(['Generated on', dayjs().format('MMMM D, YYYY')]);
  dateRow.getCell(1).font = { bold: true };

  // Total units
  const totalRow = sheet.addRow(['Total Units', unitsData.length]);
  totalRow.getCell(1).font = { bold: true };

  sheet.addRow([]);

  // Header row
  const headerRow = sheet.addRow([
    'Unit', 'BR', 'Sqft',
    'Current Rent', 'Proposed Rent', 'Rent Change', 'Rent %',
    'Current Eff.', 'Proposed Eff.', 'Eff. Change', 'Eff. %',
    'Current Gross PSF', 'Proposed Gross PSF', 'Current Net PSF', 'Proposed Net PSF',
    'Conc. ($)', 'Conc. (mo)',
    'Length', 'Current Start', 'Current End', 'Proposed Start', 'Proposed End',
  ]);
  applyHeaderStyle(headerRow);

  // Data rows
  unitsData.forEach(({ unit, currentLease, currentMetrics, proposedMetrics }) => {
    const rentChange = proposedMetrics.monthlyRent - currentMetrics.monthlyRent;
    const rentChangePercent = (rentChange / currentMetrics.monthlyRent) * 100;
    const effRentChange = proposedMetrics.effectiveMonthlyRent - currentMetrics.effectiveMonthlyRent;
    const effRentChangePercent = (effRentChange / currentMetrics.effectiveMonthlyRent) * 100;

    // Current lease dates
    const currentStartDate = convertPseudoDateToRealDate(currentLease.leaseStartDate as any);
    const currentEndDate = convertPseudoDateToRealDate(currentLease.leaseEndDate as any);
    const currentStartStr = currentStartDate ? dayjs(currentStartDate).format('MM/DD/YYYY') : '-';
    const currentEndStr = currentEndDate ? dayjs(currentEndDate).format('MM/DD/YYYY') : '-';

    // Proposed lease dates
    const proposedDates = calculateNewLeaseDatesFromLease(currentLease, { months: proposedMetrics.leaseLength });
    const proposedStartStr = proposedDates.startDate ? dayjs(proposedDates.startDate).format('MM/DD/YYYY') : '-';
    const proposedEndStr = proposedDates.endDate ? dayjs(proposedDates.endDate).format('MM/DD/YYYY') : '-';

    const row = sheet.addRow([
      unit.name || unit.id,
      unit.numBedrooms,
      unit.sqft,
      formatCurrency(currentMetrics.monthlyRent),
      formatCurrency(proposedMetrics.monthlyRent),
      formatCurrency(Math.round(rentChange * 100) / 100),
      `${rentChangePercent.toFixed(1)}%`,
      formatCurrency(currentMetrics.effectiveMonthlyRent),
      formatCurrency(proposedMetrics.effectiveMonthlyRent),
      formatCurrency(Math.round(effRentChange * 100) / 100),
      `${effRentChangePercent.toFixed(1)}%`,
      formatCurrency(Math.round(currentMetrics.annualRentPerSqft * 100) / 100),
      formatCurrency(Math.round(proposedMetrics.annualRentPerSqft * 100) / 100),
      formatCurrency(Math.round(currentMetrics.effectiveAnnualRentPerSqft * 100) / 100),
      formatCurrency(Math.round(proposedMetrics.effectiveAnnualRentPerSqft * 100) / 100),
      formatCurrency(proposedMetrics.concessions),
      concessionsToMonths(proposedMetrics.concessions, proposedMetrics.monthlyRent),
      formatLeaseLengthValue(proposedMetrics.leaseLength),
      currentStartStr,
      currentEndStr,
      proposedStartStr,
      proposedEndStr,
    ]);

    applyDataStyle(row);

    // Color code the change columns (cells 6-7: rent change, cells 10-11: eff. rent change)
    row.getCell(6).font = rentChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;
    row.getCell(7).font = rentChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;
    row.getCell(10).font = effRentChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;
    row.getCell(11).font = effRentChange >= 0 ? POSITIVE_FONT : NEGATIVE_FONT;

  });

  // Averages row
  if (unitsData.length > 0) {
    const avgCurrentRent = unitsData.reduce((sum, d) => sum + d.currentMetrics.monthlyRent, 0) / unitsData.length;
    const avgProposedRent = unitsData.reduce((sum, d) => sum + d.proposedMetrics.monthlyRent, 0) / unitsData.length;
    const avgCurrentEffRent = unitsData.reduce((sum, d) => sum + d.currentMetrics.effectiveMonthlyRent, 0) / unitsData.length;
    const avgProposedEffRent = unitsData.reduce((sum, d) => sum + d.proposedMetrics.effectiveMonthlyRent, 0) / unitsData.length;
    const avgRentChange = avgProposedRent - avgCurrentRent;
    const avgEffRentChange = avgProposedEffRent - avgCurrentEffRent;

    sheet.addRow([]);

    const avgCurrentGrossPSF = unitsData.reduce((sum, d) => sum + d.currentMetrics.annualRentPerSqft, 0) / unitsData.length;
    const avgProposedGrossPSF = unitsData.reduce((sum, d) => sum + d.proposedMetrics.annualRentPerSqft, 0) / unitsData.length;
    const avgCurrentNetPSF = unitsData.reduce((sum, d) => sum + d.currentMetrics.effectiveAnnualRentPerSqft, 0) / unitsData.length;
    const avgProposedNetPSF = unitsData.reduce((sum, d) => sum + d.proposedMetrics.effectiveAnnualRentPerSqft, 0) / unitsData.length;
    const avgConcessions = unitsData.reduce((sum, d) => sum + d.proposedMetrics.concessions, 0) / unitsData.length;
    const avgConcMonths = unitsData.reduce((sum, d) => sum + concessionsToMonths(d.proposedMetrics.concessions, d.proposedMetrics.monthlyRent), 0) / unitsData.length;
    const avgLeaseLength = unitsData.reduce((sum, d) => sum + d.proposedMetrics.leaseLength, 0) / unitsData.length;

    const avgRow = sheet.addRow([
      'AVERAGES', '-', '-',
      formatCurrency(Math.round(avgCurrentRent * 100) / 100),
      formatCurrency(Math.round(avgProposedRent * 100) / 100),
      formatCurrency(Math.round(avgRentChange * 100) / 100),
      `${((avgRentChange / avgCurrentRent) * 100).toFixed(1)}%`,
      formatCurrency(Math.round(avgCurrentEffRent * 100) / 100),
      formatCurrency(Math.round(avgProposedEffRent * 100) / 100),
      formatCurrency(Math.round(avgEffRentChange * 100) / 100),
      `${((avgEffRentChange / avgCurrentEffRent) * 100).toFixed(1)}%`,
      formatCurrency(Math.round(avgCurrentGrossPSF * 100) / 100),
      formatCurrency(Math.round(avgProposedGrossPSF * 100) / 100),
      formatCurrency(Math.round(avgCurrentNetPSF * 100) / 100),
      formatCurrency(Math.round(avgProposedNetPSF * 100) / 100),
      formatCurrency(Math.round(avgConcessions * 100) / 100),
      Math.round(avgConcMonths * 10) / 10,
      formatLeaseLengthValue(Math.round(avgLeaseLength * 10) / 10),
      '-', '-', '-', '-',
    ]);
    applySummaryStyle(avgRow);
  }
}

/**
 * Create individual unit sheet for multi-unit export
 */
function createUnitSheet(
  workbook: ExcelJS.Workbook,
  unit: Unit,
  currentMetrics: CurrentMetrics,
  proposedMetrics: ProposedMetrics,
  allUnits: Unit[],
  leaseMap: Map<string, Lease>
): void {
  const sheetName = (unit.name || unit.id).substring(0, 31); // Sheet name max 31 chars
  const sheet = workbook.addWorksheet(sheetName);

  // Set column widths (wider to accommodate detailed comparison tables)
  sheet.columns = [
    { width: 25 },
    { width: 14 },
    { width: 14 },
    { width: 12 },
    { width: 12 },
    { width: 14 },
    { width: 12 },
    { width: 12 },
  ];

  // Title
  const titleRow = sheet.addRow([`${unit.name || unit.id} - PRICING ANALYSIS`]);
  titleRow.getCell(1).font = TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 5);
  titleRow.height = 28;

  sheet.addRow([]);

  // Unit Information
  const unitInfoRow = sheet.addRow(['Unit Information']);
  unitInfoRow.getCell(1).font = SECTION_TITLE_FONT;

  const unitNameRow = sheet.addRow(['Unit Name', unit.name || unit.id]);
  unitNameRow.getCell(1).font = { bold: true };
  unitNameRow.eachCell((cell) => { cell.border = ALL_BORDERS; });

  const bedroomsRow = sheet.addRow(['Bedrooms', unit.numBedrooms]);
  bedroomsRow.getCell(1).font = { bold: true };
  bedroomsRow.eachCell((cell) => { cell.border = ALL_BORDERS; });

  const sqftRow = sheet.addRow(['Square Feet', unit.sqft]);
  sqftRow.getCell(1).font = { bold: true };
  sqftRow.eachCell((cell) => { cell.border = ALL_BORDERS; });

  sheet.addRow([]);

  // Comparison table header
  const headerRow = sheet.addRow(['Metric', 'Current', 'Proposed', 'Change', 'Change %']);
  applyHeaderStyle(headerRow);

  // Key metrics
  const metricsData = [
    { metric: 'Monthly Rent', current: currentMetrics.monthlyRent, proposed: proposedMetrics.monthlyRent, isCurrency: true },
    { metric: 'Effective Monthly Rent', current: currentMetrics.effectiveMonthlyRent, proposed: proposedMetrics.effectiveMonthlyRent, isCurrency: true },
    { metric: 'Gross PSF', current: currentMetrics.annualRentPerSqft, proposed: proposedMetrics.annualRentPerSqft, isCurrency: true },
    { metric: 'Net PSF', current: currentMetrics.effectiveAnnualRentPerSqft, proposed: proposedMetrics.effectiveAnnualRentPerSqft, isCurrency: true },
    { metric: 'Lease Length', current: currentMetrics.leaseLength, proposed: proposedMetrics.leaseLength, isCurrency: false },
    { metric: 'Concessions ($)', current: currentMetrics.concessions, proposed: proposedMetrics.concessions, isCurrency: true, invertPositive: true },
    { metric: 'Concessions (mo)', current: concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent), proposed: concessionsToMonths(proposedMetrics.concessions, proposedMetrics.monthlyRent), isCurrency: false, invertPositive: true },
  ];

  metricsData.forEach((data) => {
    const change = data.proposed - data.current;
    const changePercent = data.current !== 0 ? (change / data.current) * 100 : 0;
    const isPositive = data.invertPositive ? change <= 0 : change >= 0;

    const isLeaseLength = data.metric === 'Lease Length';
    const formatValue = (v: number) =>
      data.isCurrency ? formatCurrency(v) : isLeaseLength ? formatLeaseLengthValue(v) : v;

    const row = sheet.addRow([
      data.metric,
      formatValue(data.current),
      formatValue(data.proposed),
      data.isCurrency ? formatCurrency(change) : isLeaseLength ? Math.round(change) : change,
      isLeaseLength ? '-' : `${changePercent.toFixed(1)}%`,
    ]);

    applyDataStyle(row);
    row.getCell(1).font = { bold: true };

    if (!isLeaseLength) {
      row.getCell(4).font = isPositive ? POSITIVE_FONT : NEGATIVE_FONT;
      row.getCell(5).font = isPositive ? POSITIVE_FONT : NEGATIVE_FONT;
    }
  });

  // Comparables summary
  const comparables = getComparableUnits(unit, allUnits, leaseMap);

  if (comparables.combined.length > 0) {
    sheet.addRow([]);
    sheet.addRow([]);

    const compTitleRow = sheet.addRow(['COMPARABLE UNITS SUMMARY']);
    compTitleRow.getCell(1).font = SECTION_TITLE_FONT;
    sheet.mergeCells(compTitleRow.number, 1, compTitleRow.number, 4);

    const compHeaderRow = sheet.addRow(['Group', 'Count', 'Avg Rent', 'Avg Net PPSF']);
    applyHeaderStyle(compHeaderRow);

    const sameLineSummary = calculateGroupSummary(comparables.sameLine, leaseMap);
    const sameBedroomsSummary = calculateGroupSummary(comparables.sameBedrooms, leaseMap);
    const similarSqftSummary = calculateGroupSummary(comparables.similarSqft, leaseMap);
    const combinedSummary = calculateGroupSummary(comparables.combined, leaseMap);

    if (sameLineSummary) {
      const row = sheet.addRow(['Same Line', comparables.sameLine.length, formatCurrency(sameLineSummary.avgRent), formatCurrency(sameLineSummary.avgEffectivePPSF)]);
      applyDataStyle(row);
    }
    if (sameBedroomsSummary) {
      const row = sheet.addRow(['Same Bedrooms', comparables.sameBedrooms.length, formatCurrency(sameBedroomsSummary.avgRent), formatCurrency(sameBedroomsSummary.avgEffectivePPSF)]);
      applyDataStyle(row);
    }
    if (similarSqftSummary) {
      const row = sheet.addRow(['Similar Sqft', comparables.similarSqft.length, formatCurrency(similarSqftSummary.avgRent), formatCurrency(similarSqftSummary.avgEffectivePPSF)]);
      applyDataStyle(row);
    }
    if (combinedSummary) {
      const row = sheet.addRow(['All Comparables', comparables.combined.length, formatCurrency(combinedSummary.avgRent), formatCurrency(combinedSummary.avgEffectivePPSF)]);
      applySummaryStyle(row);
    }

    // Add detailed comparison tables
    sheet.addRow([]);
    sheet.addRow([]);

    // Same Line detailed table
    if (comparables.sameLine.length > 0) {
      addComparablesTableToSheet(sheet, 'Same Line', comparables.sameLine, leaseMap, unit.id);
    }

    // Same Bedrooms detailed table
    if (comparables.sameBedrooms.length > 0) {
      addComparablesTableToSheet(sheet, 'Same Bedrooms', comparables.sameBedrooms, leaseMap, unit.id);
    }

    // Similar Sqft detailed table
    if (comparables.similarSqft.length > 0) {
      addComparablesTableToSheet(sheet, 'Similar Sqft', comparables.similarSqft, leaseMap, unit.id);
    }
  }
}

/**
 * Download workbook as file
 */
async function downloadWorkbook(workbook: ExcelJS.Workbook, filename: string): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Export single unit pricing data to Excel
 */
export async function exportSingleUnitToSpreadsheet(
  unit: Unit,
  currentMetrics: CurrentMetrics,
  proposedMetrics: ProposedMetrics,
  allUnits: Unit[],
  leaseMap: Map<string, Lease>,
  allLeases?: Lease[]
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'UrbanMix';
  workbook.created = new Date();

  // Sheet 1: Before/After Comparison
  createBeforeAfterSheet(workbook, unit, currentMetrics, proposedMetrics);

  // Get comparable units
  const comparables = getComparableUnits(unit, allUnits, leaseMap);

  // Sheet 2: Same Line Comparables
  if (comparables.sameLine.length > 0) {
    const sameLineSummary = calculateGroupSummary(comparables.sameLine, leaseMap);
    createComparablesSheet(workbook, 'Same Line', 'Same Line', comparables.sameLine, leaseMap, sameLineSummary);
  }

  // Sheet 3: Same Bedrooms Comparables
  if (comparables.sameBedrooms.length > 0) {
    const sameBedroomsSummary = calculateGroupSummary(comparables.sameBedrooms, leaseMap);
    createComparablesSheet(workbook, 'Same Bedrooms', 'Same Bedrooms', comparables.sameBedrooms, leaseMap, sameBedroomsSummary);
  }

  // Sheet 4: Similar Sqft Comparables
  if (comparables.similarSqft.length > 0) {
    const similarSqftSummary = calculateGroupSummary(comparables.similarSqft, leaseMap);
    createComparablesSheet(workbook, 'Similar Sqft', 'Similar Sqft', comparables.similarSqft, leaseMap, similarSqftSummary);
  }

  // Sheet 5: Combined Comparables
  if (comparables.combined.length > 0) {
    const combinedSummary = calculateGroupSummary(comparables.combined, leaseMap);
    createComparablesSheet(workbook, 'All Comparables', 'Combined', comparables.combined, leaseMap, combinedSummary);
  }

  // Sheet 6: Market Trends (if lease data available)
  if (allLeases && allLeases.length > 0) {
    const unitMap = new Map<string, Unit>();
    allUnits.forEach((u) => unitMap.set(u.id, u));
    const trends = analyzeTrends(allLeases, unitMap);
    if (trends) {
      createTrendsSheet(workbook, trends);
    }
  }

  // Generate filename and download
  const unitName = (unit.name || unit.id).replace(/[^a-zA-Z0-9]/g, '_');
  const timestamp = dayjs().format('YYYY-MM-DD');
  const filename = `Pricing_${unitName}_${timestamp}.xlsx`;

  await downloadWorkbook(workbook, filename);
}

/**
 * Export multiple units (draft leases) to Excel with multiple worksheets
 */
export async function exportMultipleUnitsToSpreadsheet(
  unitsData: UnitPricingData[],
  allUnits: Unit[],
  leaseMap: Map<string, Lease>,
  allLeases?: Lease[],
  buildingName?: string
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'UrbanMix';
  workbook.created = new Date();

  // Sheet 1: Summary of all units
  createMultiUnitSummarySheet(workbook, unitsData);

  // Create individual sheets for each unit
  unitsData.forEach(({ unit, currentMetrics, proposedMetrics }) => {
    createUnitSheet(workbook, unit, currentMetrics, proposedMetrics, allUnits, leaseMap);
  });

  // Add market trends sheet if available
  if (allLeases && allLeases.length > 0) {
    const unitMap = new Map<string, Unit>();
    allUnits.forEach((u) => unitMap.set(u.id, u));
    const trends = analyzeTrends(allLeases, unitMap);
    if (trends) {
      createTrendsSheet(workbook, trends);
    }
  }

  // Generate filename and download
  const name = buildingName ? buildingName.replace(/[^a-zA-Z0-9]/g, '_') : 'Building';
  const timestamp = dayjs().format('YYYY-MM-DD');
  const filename = `Draft_Leases_${name}_${timestamp}.xlsx`;

  await downloadWorkbook(workbook, filename);
}

/**
 * Calculate proposed metrics from current metrics and draft lease
 */
export function calculateProposedMetricsFromDraft(
  unit: Unit,
  currentMetrics: CurrentMetrics,
  draftLease: Lease
): ProposedMetrics {
  const leaseLength = calculateLeaseLength(draftLease) || 12;
  const sqft = unit.sqft || 1;

  const totalConcessions = calculateConcessions(draftLease);

  const effectiveMonthlyRent = leaseLength > 0
    ? Math.round((draftLease.monthlyRent - (totalConcessions / leaseLength)) * 100) / 100
    : draftLease.monthlyRent;
  const effectiveAnnualRent = effectiveMonthlyRent * 12;
  const effectiveAnnualRentPerSqft = Math.round((effectiveAnnualRent / sqft) * 100) / 100;
  const annualRent = draftLease.monthlyRent * 12;
  const annualRentPerSqft = annualRent / sqft;

  const netIncreaseAmount = effectiveMonthlyRent - currentMetrics.effectiveMonthlyRent;
  const netIncreasePercent = (netIncreaseAmount / currentMetrics.effectiveMonthlyRent) * 100;

  return {
    monthlyRent: Math.round(draftLease.monthlyRent * 100) / 100,
    leaseLength,
    concessions: totalConcessions,
    effectiveMonthlyRent,
    effectiveAnnualRent: Math.round(effectiveAnnualRent * 100) / 100,
    effectiveAnnualRentPerSqft,
    annualRent: Math.round(annualRent * 100) / 100,
    annualRentPerSqft: Math.round(annualRentPerSqft * 100) / 100,
    netIncreaseAmount: Math.round(netIncreaseAmount * 100) / 100,
    netIncreasePercent: Math.round(netIncreasePercent * 100) / 100,
  };
}

/**
 * Export leases to Excel - Standard format with processed data
 */
export async function exportLeasesToSpreadsheetStandard(
  leases: Lease[],
  unitMap: Map<string, Unit>,
  buildingName?: string,
  /**
   * Live unit status per unitId, folded from the `state_*` feed. Optional — when
   * absent the status columns export as '-' rather than the sheet losing shape.
   */
  statusByUnitId?: Record<string, ResolvedUnitStatus>
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'UrbanMix';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Leases');

  // Set column widths
  sheet.columns = [
    { width: 15 }, // Apartment ID
    { width: 10 }, // Bedrooms
    { width: 14 }, // Lease Start Date
    { width: 14 }, // Lease End Date
    { width: 12 }, // Lease Duration (Months)
    { width: 12 }, // Square Footage
    { width: 14 }, // Monthly Rent
    { width: 14 }, // Monthly Rent per Sqft
    { width: 14 }, // Amenities Fee
    { width: 18 }, // Amenities Opt-In Status
    { width: 14 }, // Pool Access Fee
    { width: 18 }, // Pool Access Opt-In Status
    { width: 14 }, // Security Deposit
    { width: 16 }, // Total Monthly Charge
    { width: 25 }, // Concessions Details
    { width: 14 }, // Concession Year
    { width: 14 }, // Concession Months
    { width: 18 }, // Total Concession Amount ($)
    { width: 16 }, // Total Lease Rent
    { width: 18 }, // Effective Rent (After Concessions)
    { width: 18 }, // Effective Monthly Rent
    { width: 16 }, // Effective Rent per Sqft
    { width: 10 }, // Has Pets
    { width: 16 }, // Lease Created Date
    { width: 30 }, // Document Id
    { width: 12 }, // Occupancy
    { width: 22 }, // Unit Status
    { width: 30 }, // Current Tenant Status
    { width: 30 }, // Next Tenant Status
  ];

  // Title
  const titleRow = sheet.addRow(['LEASE DATA EXPORT - STANDARD FORMAT']);
  titleRow.getCell(1).font = TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 10);
  titleRow.height = 28;

  sheet.addRow([]);

  // Generated date and count
  const dateRow = sheet.addRow(['Generated on', dayjs().format('MMMM D, YYYY')]);
  dateRow.getCell(1).font = { bold: true };

  const totalRow = sheet.addRow(['Total Leases', leases.length]);
  totalRow.getCell(1).font = { bold: true };

  sheet.addRow([]);

  // Header row
  const headerRow = sheet.addRow([
    'Apartment ID',
    'Bedrooms',
    'Lease Start Date',
    'Lease End Date',
    'Lease Duration (Months)',
    'Square Footage',
    'Monthly Rent',
    'Monthly Rent per Sqft',
    'Amenities Fee',
    'Amenities Opt-In Status',
    'Pool Access Fee',
    'Pool Access Opt-In Status',
    'Security Deposit',
    'Total Monthly Charge',
    'Concessions Details',
    'Concession Year',
    'Concession Months',
    'Total Concession Amount ($)',
    'Total Lease Rent',
    'Effective Rent (After Concessions)',
    'Effective Monthly Rent',
    'Effective Rent per Sqft',
    'Has Pets',
    'Lease Created Date',
    'Document Id',
    'Occupancy',
    'Unit Status',
    'Current Tenant Status',
    'Next Tenant Status',
  ]);
  applyHeaderStyle(headerRow);

  // Data rows
  leases.forEach((lease) => {
    const unit = unitMap.get(lease.unitId);
    const sqft = unit?.sqft || 0;

    const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
    const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);
    const leaseLength = calculateLeaseLengthFromDates(startDate, endDate);

    // Calculate fees
    const fees = lease.fees || {};
    const amenitiesFee = fees['amenities'] || fees['Amenities'] || 0;
    const poolAccessFee = fees['pool'] || fees['Pool'] || fees['poolAccess'] || fees['Pool Access'] || 0;
    const totalFees = Object.values(fees).reduce((sum, fee) => sum + (fee as number), 0);

    // Calculate concessions
    const concessions = Array.isArray(lease.concessions) ? lease.concessions : [];
    const totalConcessions = calculateConcessions(lease);

    // Format concession details
    const concessionDetails = concessions.map((c: any) => {
      let amountStr: string;
      if (c.type === 'fixed') {
        amountStr = '$' + c.amount;
      } else if (c.type === 'percentage') {
        amountStr = c.amount + '%';
      } else {
        amountStr = c.amount + '×rent';
      }
      const desc = c.description ? ` (${c.description})` : '';
      return `${c.month}/${c.year}: ${amountStr}${desc}`;
    }).join('; ') || 'None';

    // Get concession years and months
    const concessionYears = [...new Set(concessions.map((c: any) => c.year))].join(', ') || '-';
    const concessionMonths = concessions.map((c: any) => c.month).join(', ') || '-';

    // Calculate totals
    const totalMonthlyCharge = lease.monthlyRent + totalFees;
    const totalLeaseRent = lease.monthlyRent * leaseLength;
    const effectiveRentAfterConcessions = totalLeaseRent - totalConcessions;
    const effectiveMonthlyRent = leaseLength > 0
      ? Math.round((lease.monthlyRent - (totalConcessions / leaseLength)) * 100) / 100
      : lease.monthlyRent;
    const monthlyRentPerSqft = sqft > 0 ? Math.round((lease.monthlyRent / sqft) * 100) / 100 : 0;
    const effectiveRentPerSqft = sqft > 0 ? Math.round(((effectiveMonthlyRent * 12) / sqft) * 100) / 100 : 0;

    // Check for pets
    const hasPets = (lease.petIds && lease.petIds.length > 0) ? 'Yes' : 'No';

    // Get created date
    const createdDate = lease.createdAt
      ? dayjs(lease.createdAt instanceof Date ? lease.createdAt : new Date(lease.createdAt as any)).format('MM/DD/YYYY')
      : '-';

    // Live unit status. Note this describes the UNIT, not this lease row — a
    // unit with several exported leases repeats the same status on each.
    const status = statusByUnitId?.[lease.unitId];
    const occupancy = status ? (status.occupancy === 'occupied' ? 'Occupied' : 'Vacant') : '-';
    const unitStatus = status ? unitStatusGroup(status) : '-';
    const currentTenantStatus = status ? currentTenantStatusLabel(status) || '-' : '-';
    const nextTenantStatus = status ? nextTenantStatusLabel(status) || '-' : '-';

    const row = sheet.addRow([
      unit?.name || lease.unitId,
      unit?.numBedrooms ?? '-',
      startDate ? dayjs(startDate).format('MM/DD/YYYY') : '-',
      endDate ? dayjs(endDate).format('MM/DD/YYYY') : '-',
      formatLeaseLengthFromDates(startDate, endDate),
      sqft,
      formatCurrency(lease.monthlyRent),
      sqft > 0 ? formatCurrency(monthlyRentPerSqft) : '-',
      formatCurrency(amenitiesFee),
      amenitiesFee > 0 ? 'Yes' : 'No',
      formatCurrency(poolAccessFee),
      poolAccessFee > 0 ? 'Yes' : 'No',
      formatCurrency(lease.securityDeposit || 0),
      formatCurrency(totalMonthlyCharge),
      concessionDetails,
      concessionYears,
      concessionMonths,
      formatCurrency(Math.round(totalConcessions * 100) / 100),
      formatCurrency(Math.round(totalLeaseRent * 100) / 100),
      formatCurrency(Math.round(effectiveRentAfterConcessions * 100) / 100),
      formatCurrency(effectiveMonthlyRent),
      formatCurrency(effectiveRentPerSqft),
      hasPets,
      createdDate,
      lease.docusignEnvelopeId || '-',
      occupancy,
      unitStatus,
      currentTenantStatus,
      nextTenantStatus,
    ]);

    applyDataStyle(row);
  });

  // Generate filename and download
  const name = buildingName ? buildingName.replace(/[^a-zA-Z0-9]/g, '_') : 'Building';
  const timestamp = dayjs().format('YYYY-MM-DD');
  const filename = `Leases_Standard_${name}_${timestamp}.xlsx`;

  await downloadWorkbook(workbook, filename);
}

/**
 * Flatten a nested object into dot-notation keys
 * e.g., { leaseBaseData: { rent: 1000 } } => { 'leaseBaseData.rent': 1000 }
 */
function flattenObject(obj: any, prefix = ''): Record<string, any> {
  const result: Record<string, any> = {};

  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      // Recursively flatten nested objects
      Object.assign(result, flattenObject(value, newKey));
    } else if (Array.isArray(value)) {
      // For arrays, stringify them
      result[newKey] = JSON.stringify(value);
    } else {
      result[newKey] = value;
    }
  }

  return result;
}

/**
 * Export leases to Excel - Raw format with eLeaseFields data flattened
 */
export async function exportLeasesToSpreadsheetRaw(
  leases: Lease[],
  unitMap: Map<string, Unit>,
  buildingName?: string
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'UrbanMix';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Raw Lease Data');

  // Always include these core fields first
  const coreFields = [
    'leaseId',
    'unitId',
    'unitName',
    'leaseStartDate',
    'leaseEndDate',
    'monthlyRent',
    'securityDeposit',
    'status',
    'docusignEnvelopeId',
  ];

  // First pass: flatten all eLeaseFields and collect unique keys
  const allFlattenedFields = new Set<string>();
  const flattenedDataByLease = new Map<string, Record<string, any>>();

  leases.forEach((lease) => {
    const eLeaseFields = (lease as any).eLeaseFields || {};
    const flattened = flattenObject(eLeaseFields);
    flattenedDataByLease.set(lease.id, flattened);

    Object.keys(flattened).forEach((key) => allFlattenedFields.add(key));
  });

  // Convert to sorted array
  const flattenedFieldNames = Array.from(allFlattenedFields).sort();

  // All columns: core fields + flattened eLeaseFields
  const allColumns = [...coreFields, ...flattenedFieldNames];

  // Set column widths - use field name length but cap at reasonable width
  sheet.columns = allColumns.map((field) => ({
    width: Math.min(40, Math.max(15, field.length + 2))
  }));

  // Title
  const titleRow = sheet.addRow(['RAW LEASE DATA EXPORT (eLeaseFields)']);
  titleRow.getCell(1).font = TITLE_FONT;
  sheet.mergeCells(titleRow.number, 1, titleRow.number, Math.min(10, allColumns.length));
  titleRow.height = 28;

  sheet.addRow([]);

  // Generated date and count
  const dateRow = sheet.addRow(['Generated on', dayjs().format('MMMM D, YYYY')]);
  dateRow.getCell(1).font = { bold: true };

  const totalRow = sheet.addRow(['Total Leases', leases.length]);
  totalRow.getCell(1).font = { bold: true };

  const fieldsRow = sheet.addRow(['Total Fields', flattenedFieldNames.length + coreFields.length]);
  fieldsRow.getCell(1).font = { bold: true };

  sheet.addRow([]);

  // Header row
  const headerRow = sheet.addRow(allColumns);
  applyHeaderStyle(headerRow);

  // Data rows
  leases.forEach((lease) => {
    const unit = unitMap.get(lease.unitId);
    const flattenedData = flattenedDataByLease.get(lease.id) || {};

    const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
    const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);

    // Build row data
    const rowData: any[] = [];

    // Core fields
    rowData.push(lease.id);
    rowData.push(lease.unitId);
    rowData.push(unit?.name || lease.unitId);
    rowData.push(startDate ? dayjs(startDate).format('MM/DD/YYYY') : '-');
    rowData.push(endDate ? dayjs(endDate).format('MM/DD/YYYY') : '-');
    rowData.push(lease.monthlyRent);
    rowData.push(lease.securityDeposit || 0);
    rowData.push(lease.status || 'valid');
    rowData.push(lease.docusignEnvelopeId || '-');

    // Flattened eLeaseFields
    flattenedFieldNames.forEach((fieldName) => {
      const value = flattenedData[fieldName];
      if (value === undefined || value === null) {
        rowData.push('');
      } else {
        rowData.push(value);
      }
    });

    const row = sheet.addRow(rowData);
    applyDataStyle(row);
  });

  // Generate filename and download
  const name = buildingName ? buildingName.replace(/[^a-zA-Z0-9]/g, '_') : 'Building';
  const timestamp = dayjs().format('YYYY-MM-DD');
  const filename = `Leases_Raw_${name}_${timestamp}.xlsx`;

  await downloadWorkbook(workbook, filename);
}
